import {Exist} from "components/Exist";
import {IconClose} from "components/Icons/IconClose";
import {toggleBodyClass} from "modules/utils/dom_helpers";
import React, {Fragment, useEffect, PropsWithChildren, useRef} from "react";
import styled from "@emotion/styled";
import {useDialogModal} from "modules/utils";
import reactNodeToString from "react-node-to-string";

const BlurBackground = styled.div`
	@keyframes blurAnimation {
		from {
			backdrop-filter: blur(0px);
		}
		to {
			backdrop-filter: blur(7px);
		}
	}
	width: 100vw;
	height: 100vh;
	position: absolute;
	left: 0;
	top: 0;
	content: "";
	display: block;
	background: rgba(35, 35, 35, 0.5);
	backdrop-filter: blur(0);
	z-index: 13;
	animation: blurAnimation 0.3s ease forwards;
`;

const Modal = styled.dialog<{background?: string}>`
	position: fixed;
	top: 50%;
	left: 50%;
	width: 100%;
	transform: translateX(-50%) translateY(-50%);
	background: ${({background}) => (background ? background : "#090b0f")};
	background-size: cover;
	display: flex;
	flex-flow: column;
	z-index: 16;
	transition: all 0.3s ease-in-out;
	max-width: 400px;
	max-height: calc(100% - 100px);
	min-height: 620px;

	&.close {
		display: none;
		visibility: hidden;
	}
`;

const Header = styled.div`
	//background: #090b0f;
	color: #fff;
	padding: 20px;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	font-style: normal;
	font-weight: bold;
	font-size: 20px;
	line-height: 24px;
	text-align: center;
	font-family: var(--fontFamilyBase);

	svg {
		width: 20px;
		height: 20px;
	}
`;

const Button = styled.button`
	margin-left: auto;
	position: absolute;
	top: 5px;
	right: 0;
	padding: 20px;
	box-sizing: content-box;
	background: none;
	box-shadow: none;
	border: none;
	color: #fff;
`;

interface IProps {
	isOpen: boolean;
	headerText?: React.ReactElement | string;
	onClose?: () => void;
	name: string;
	background?: string;
}

export const HOCModalCenterDesktopPosition: React.FC<PropsWithChildren<IProps>> = (props) => {
	const modalRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		toggleBodyClass("fixed", props.isOpen, props.name);
		return () => {
			toggleBodyClass("fixed", false, props.name);
		};
	}, [props.isOpen]);

	useDialogModal(modalRef, props.isOpen);

	const onClose = () => {
		props?.onClose?.();
		modalRef.current?.close();
	};

	const closeButtonLabel = `Close ${reactNodeToString(props.headerText || "")} dialog`;

	return (
		<Fragment>
			{props.isOpen && <BlurBackground />}
			<Modal
				onClose={onClose}
				className={`safe-area-bottom ${props.isOpen ? "open" : "close"}`}
				background={props.background}
				ref={modalRef}
				aria-live={"polite"}
				aria-atomic={true}
				aria-hidden={!props.isOpen}
			>
				<Header role={"heading"}>
					{props.onClose && (
						<Button aria-label={closeButtonLabel} onClick={onClose} tabIndex={0}>
							<IconClose />
						</Button>
					)}
					<Exist when={!!props.headerText}>{props.headerText}</Exist>
				</Header>

				{props.children}
			</Modal>
		</Fragment>
	);
};
